import {
  ArrowRightOutlined,
  BorderOuterOutlined,
  DeleteFilled,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  FilterOutlined,
  SyncOutlined,
  DownOutlined,
  ExportOutlined,
  ImportOutlined,
  UploadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Row,
  Col,
  Select,
  Input,
  InputNumber,
  Radio,
  Button,
  Space,
  Form,
  Popconfirm,
  Table,
  Tooltip,
  Tag,
  message,
  Modal,
  DatePicker,
  Dropdown,
  Menu,
  Alert,
  Checkbox,
} from "antd";
import { useEffect, useRef, useState } from "react";
import "../Pages/CSS/DonnesBases.css";
import "../Pages/CSS/Traitement.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ParticipantsIpmService } from "../Services/ParticipantsIpmService";
import AdvanceSearchModal from "./AdvanceSearchModal";
import { FamilleService } from "../Services/FamilleService";
import moment from "moment";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { printpdf } from "../Services/PrintService";
import { AdherentsService } from "../Services/AdherentsService";
import AdvanceSearchModalPart from "./AdvanceSearchModalPart";

// Colonnes attendues (noms et ordre) dans le fichier Excel d'import des participants
const IMPORT_PARTICIPANT_COLUMNS = [
  "codesoc",
  "matricule",
  "nom",
  "prenom",
  "sexe",
  "sitfam",
  "datenaiss",
  "lieunaiss",
  "adresse",
  "portable",
  "email",
  "cin",
  "dateemb",
  "numfiche",
  "typecontrat",
  "categprof",
  "emploi",
];

// Colonnes obligatoires (les autres peuvent être vides, comme dans le modèle fourni)
const REQUIRED_PARTICIPANT_COLUMNS = [
  "codesoc",
  "matricule",
  "nom",
  "prenom",
  "sexe",
  "sitfam",
  "datenaiss",
];

// Parse une valeur de cellule date (Date Excel, ISO ou DD/MM/YYYY) en objet dayjs, sans plugin customParseFormat
const parseImportDate = (value) => {
  if (value === "" || value === undefined || value === null) return null;
  if (dayjs.isDayjs(value)) return value.isValid() ? value : null;

  if (value instanceof Date) {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  }

  const raw = String(value).trim();
  if (!raw) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const parsed = dayjs(raw);
    return parsed.isValid() ? parsed : null;
  }

  const frMatch = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (frMatch) {
    const [, dd, mm, yyyy] = frMatch;
    const parsed = dayjs(`${yyyy}-${mm}-${dd}`);
    return parsed.isValid() ? parsed : null;
  }

  return null;
};

const { RangePicker } = DatePicker;
const { confirm } = Modal;

const ParticipantIpm = () => {
  const navigate = useNavigate();

  const formattedDate = (date) => moment(date).format("DD/MM/YYYY");
  const formattedDateTime = (date) => date ? moment(date).format("DD/MM/YYYY HH:mm") : "";

  // Fonctions helper pour le filtre de date de création
  const isDateInRangeInclusive = (d, start, end) => {
    if (!d || !start || !end) return true;
    return (d.isSame(start, "day") || d.isAfter(start, "day")) &&
      (d.isSame(end, "day") || d.isBefore(end, "day"));
  };

  const parseDateCreation = (value) => {
    if (!value) return null;
    if (dayjs.isDayjs(value)) return value;
    if (value instanceof Date) return dayjs(value);
    const raw = String(value).trim();
    const formats = [
      "YYYY-MM-DD",
      "YYYY-MM-DDTHH:mm:ss",
      "YYYY-MM-DDTHH:mm:ss.SSS",
      "YYYY-MM-DDTHH:mm:ssZ",
      "DD/MM/YYYY",
      "DD-MM-YYYY",
    ];
    for (const fmt of formats) {
      const parsed = dayjs(raw, fmt, true);
      if (parsed.isValid()) return parsed;
    }
    const fallback = dayjs(raw);
    return fallback.isValid() ? fallback : null;
  };

  const getDateCreationRangeForPreset = (presetKey) => {
    const now = dayjs();
    switch (presetKey) {
      case "today": return [now.startOf("day"), now.endOf("day")];
      case "yesterday": {
        const y = now.subtract(1, "day");
        return [y.startOf("day"), y.endOf("day")];
      }
      case "this_week": return [now.startOf("week"), now.endOf("week")];
      case "last_week": {
        const lw = now.subtract(1, "week");
        return [lw.startOf("week"), lw.endOf("week")];
      }
      case "this_month": return [now.startOf("month"), now.endOf("month")];
      case "last_month": {
        const lm = now.subtract(1, "month");
        return [lm.startOf("month"), lm.endOf("month")];
      }
      case "this_year": return [now.startOf("year"), now.endOf("year")];
      case "last_year": {
        const ly = now.subtract(1, "year");
        return [ly.startOf("year"), ly.endOf("year")];
      }
      default: return [null, null];
    }
  };

  const buildDateCreationFilterKey = (filterKey, range) => {
    if (!filterKey) return null;
    if (filterKey !== "range") return filterKey;
    const start = range?.[0];
    const end = range?.[1];
    if (!start || !end) return null;
    const startStr = dayjs.isDayjs(start) ? start.format("YYYY-MM-DD") : "";
    const endStr = dayjs.isDayjs(end) ? end.format("YYYY-MM-DD") : "";
    return `range:${startStr}:${endStr}`;
  };

  const matchDateCreationByFilterValue = (filterValue, recordDate) => {
    const key = String(filterValue || "");
    if (key.startsWith("range:")) {
      const [, startStr, endStr] = key.split(":");
      if (!startStr || !endStr) return true;
      const start = dayjs(startStr, "YYYY-MM-DD").startOf("day");
      const end = dayjs(endStr, "YYYY-MM-DD").endOf("day");
      return isDateInRangeInclusive(recordDate, start, end);
    }
    const [start, end] = getDateCreationRangeForPreset(key);
    if (!start || !end) return true;
    return isDateInRangeInclusive(recordDate, start, end);
  };

  const initialTableData = [];
  // const [showEditForm, setShowEditForm] = useState(false);
  const [totalParticipants, setTotalParticipants] = useState(0);
  const [nbenfant, setNbenfant] = useState(0);
  const [nbfemme, setNbfemme] = useState(0);
  const [nbepoux, setNbepoux] = useState(0);
  const [advancedSearchActivated, setAdvancedSearchActivated] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [codesocOptions, setCodesocOptions] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const location = useLocation();
  const [selectedCodesoc, setSelectedCodesoc] = useState(null);
  const [showCreateDivButton, setShowCreateDivButton] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState("a");
  const [tableData, setTableData] = useState(initialTableData);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [filteredData, setFilteredData] = useState(initialTableData);
  const [form] = Form.useForm();

  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [importRows, setImportRows] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const importFileInputRef = useRef(null);

  // États pour le filtre de date de création
  const [dateCreationFilter, setDateCreationFilter] = useState(null);
  const [dateCreationRange, setDateCreationRange] = useState([null, null]);
  const [dateCreationDraftFilter, setDateCreationDraftFilter] = useState(null);
  const [dateCreationDraftRange, setDateCreationDraftRange] = useState([null, null]);

  // États pour le filtre d'âge
  const [ageMin, setAgeMin] = useState(null);
  const [ageMax, setAgeMax] = useState(null);
  const [ageMinDraft, setAgeMinDraft] = useState(null);
  const [ageMaxDraft, setAgeMaxDraft] = useState(null);

  // État pour le filtre de sexe
  const [sexeFilter, setSexeFilter] = useState([]);

  // État pour le compteur de lignes
  const [rowCount, setRowCount] = useState(0);
  const [currentPageData, setCurrentPageData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedParticipants, setSelectedParticipants] = useState([]);

  const participantsIpmService = new ParticipantsIpmService();
  const adherentsService = new AdherentsService();

  const codeSocFromPart = location.state?.codesocFromPart;

  // 1. Initialisation de selectedCodesoc depuis la location (une seule fois)
  useEffect(() => {
    if (codeSocFromPart !== null && codeSocFromPart !== undefined) {
      setSelectedCodesoc(codeSocFromPart);
      console.log("CodeSoc from location:", codeSocFromPart);

    }
  }, [codeSocFromPart]);

  // 2. Chargement des sociétés et options (ne touche pas selectedCodesoc ici)
  useEffect(() => {
    const fetchSocietes = async () => {
      setLoading(true);
      try {
        const participantData = await adherentsService.getAllAdherents();
        // const participantData = participantData.filter(
        //   (participant) => !participant.suspension_adht
        // );
        setCodesocOptions(
          [...new Set(participantData.map((p) => p.codeadht))].map((codeadht) => ({
            codesoc: codeadht,
            societe: participantData.find((p) => p.codeadht === codeadht)?.intitule || "Unknown",
          }))
        );
        // Si selectedCodesoc n'est pas encore défini, on prend la première société valide
        setSelectedCodesoc(codeSocFromPart ? codeSocFromPart : (participantData[0]?.codeadht || null));
        // if (!selectedCodesoc && participantData.length > 0) {
        //   setSelectedCodesoc(participantData[0].codeadht);
        //   console.log('selectedCodeSoc', codeSocFromPart);

        // }
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    fetchSocietes();
    // eslint-disable-next-line
  }, []);

  // 3. Chargement du tableau à chaque changement de selectedCodesoc
  useEffect(() => {
    if (!selectedCodesoc) return;
    setLoading(true);
    participantsIpmService.getAllParticipantsIpm(selectedCodesoc)
      .then((participantsData) => {
        setTableData(participantsData);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [selectedCodesoc]);


  useEffect(() => {
    setFilteredData(tableData); // Initialisation des données filtrées avec les données complètes au chargement
  }, [tableData]);



  /*   useEffect(() => {
    setFilteredData(tableData); // Initialisation des données filtrées avec les données complètes au chargement
  }, [tableData]); */

  const handleSearch = (value) => {
    const filtered = tableData.filter((item) =>
      Object.values(item).some(
        (val) =>
          val && val.toString().toLowerCase().includes(value.toLowerCase())
      )
    );

    setFilteredData(filtered);

    form.validateFields().then((values) => {
      setAdvancedSearchActivated(true);
    });
  };

  //Actualisation
  const handleResetAll = async () => {
    setLoading(true);
    try {
      const updatedData = await participantsIpmService.getAllParticipantsIpm(
        selectedCodesoc
      ); // Récupère les nouvelles données
      setTableData(updatedData); // Met à jour le tableau avec les nouvelles données
      setSelectedRowData(null); // Réinitialise les données sélectionnées
      setLoading(false);
    } catch (error) { }
  };

  const handleExportExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Participants");
    XLSX.writeFile(workbook, "Participants.xlsx");
  };

  const handleExportPDF = async () => {
    try {
      await participantsIpmService.printList(filteredData);
      printpdf("/Print/Exports/Tab/List_participants");
    } catch (error) {
      console.error("Erreur lors de l'exportation en PDF :", error);
    }
  };

  const handleOpenImportModal = () => {
    setImportRows([]);
    setImportErrors([]);
    setImportResult(null);
    setIsImportModalVisible(true);
  };

  const handleCloseImportModal = () => {
    setIsImportModalVisible(false);
    setImportRows([]);
    setImportErrors([]);
    setImportResult(null);
    if (importFileInputRef.current) {
      importFileInputRef.current.value = "";
    }
  };

  const handleImportFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array", cellDates: true });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const rawRows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "" });

        if (rawRows.length === 0) {
          setImportRows([]);
          setImportErrors([{ rowNum: "-", field: "-", message: "Le fichier est vide." }]);
          return;
        }

        const headerRow = rawRows[0].map((h) => (h || "").toString().trim());
        const missingColumns = IMPORT_PARTICIPANT_COLUMNS.filter((col) => !headerRow.includes(col));

        if (missingColumns.length > 0) {
          setImportRows([]);
          setImportErrors([{
            rowNum: "-",
            field: "-",
            message: `Colonne(s) manquante(s) : ${missingColumns.join(", ")}. Merci d'utiliser le modèle fourni.`,
          }]);
          return;
        }

        const dataRows = rawRows
          .slice(1)
          .filter((row) => row.some((cell) => cell !== "" && cell !== undefined && cell !== null));

        // Détection des doublons (codesoc + matricule) au sein du fichier importé
        const codesocIndex = headerRow.indexOf("codesoc");
        const matriculeIndex = headerRow.indexOf("matricule");
        const duplicateKeyOccurrences = {};
        dataRows.forEach((row) => {
          const codesocValue = String(row[codesocIndex] ?? "").trim();
          const matriculeValue = String(row[matriculeIndex] ?? "").trim();
          if (!codesocValue || !matriculeValue) return;
          const key = `${codesocValue}-${matriculeValue}`;
          duplicateKeyOccurrences[key] = (duplicateKeyOccurrences[key] || 0) + 1;
        });

        const parsedRows = [];
        const errors = [];

        dataRows.forEach((row, idx) => {
          const excelRowNum = idx + 2; // ligne 1 = entêtes
          const rowObj = { __rowNum: excelRowNum };

          IMPORT_PARTICIPANT_COLUMNS.forEach((col) => {
            const colIndex = headerRow.indexOf(col);
            const cellValue = row[colIndex];
            rowObj[col] = cellValue === undefined || cellValue === null ? "" : cellValue;
          });

          if (rowObj.codesoc !== "" && rowObj.matricule !== "") {
            const key = `${String(rowObj.codesoc).trim()}-${String(rowObj.matricule).trim()}`;
            if (duplicateKeyOccurrences[key] > 1) {
              errors.push({
                rowNum: excelRowNum,
                field: "codesoc / matricule",
                message: `Doublon dans le fichier : le couple codesoc/matricule "${key}" apparaît ${duplicateKeyOccurrences[key]} fois`,
              });
            }
          }

          REQUIRED_PARTICIPANT_COLUMNS.forEach((col) => {
            const value = rowObj[col];
            const isEmpty = value === "" || value === undefined || value === null;
            if (isEmpty) {
              errors.push({ rowNum: excelRowNum, field: col, message: `Cellule vide (${col})` });
            }
          });

          if (rowObj.datenaiss !== "") {
            const parsedDate = parseImportDate(rowObj.datenaiss);
            if (!parsedDate) {
              errors.push({
                rowNum: excelRowNum,
                field: "datenaiss",
                message: `Date de naissance incorrecte : "${rowObj.datenaiss}" (formats acceptés : YYYY-MM-DD ou DD/MM/YYYY)`,
              });
            } else {
              rowObj.datenaiss = parsedDate.format("YYYY-MM-DD");
            }
          }

          if (rowObj.dateemb !== "") {
            const parsedDate = parseImportDate(rowObj.dateemb);
            if (!parsedDate) {
              errors.push({
                rowNum: excelRowNum,
                field: "dateemb",
                message: `Date d'embauche incorrecte : "${rowObj.dateemb}" (formats acceptés : YYYY-MM-DD ou DD/MM/YYYY)`,
              });
            } else {
              rowObj.dateemb = parsedDate.format("YYYY-MM-DD");
            }
          }

          if (rowObj.numfiche !== "" && Number.isNaN(Number(rowObj.numfiche))) {
            errors.push({
              rowNum: excelRowNum,
              field: "numfiche",
              message: `N° fiche invalide : "${rowObj.numfiche}"`,
            });
          }

          parsedRows.push(rowObj);
        });

        setImportRows(parsedRows);
        setImportErrors(errors);
        setImportResult(null);
      } catch (error) {
        console.error("Erreur lors de la lecture du fichier :", error);
        setImportRows([]);
        setImportErrors([{
          rowNum: "-",
          field: "-",
          message: "Fichier illisible. Vérifiez qu'il s'agit bien d'un fichier Excel (.xlsx).",
        }]);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleExportImportErrors = () => {
    if (importErrors.length === 0) return;

    const dataToExport = importErrors.map((err) => ({
      "Ligne Excel": err.rowNum,
      Colonne: err.field,
      Anomalie: err.message,
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Anomalies");
    XLSX.writeFile(workbook, "anomalies_import_participants.xlsx");
  };

  const handleConfirmImport = async () => {
    if (importRows.length === 0 || importErrors.length > 0 || isImporting) return;

    setIsImporting(true);
    try {
      const listParticipants = importRows.map(({ __rowNum, ...row }) => ({
        ...row,
        numfiche: row.numfiche !== "" ? Number(row.numfiche) : null,
      }));

      const response = await participantsIpmService.importListParticipantsExcel(listParticipants);

      if (response?.success) {
        setImportResult({ success: true, message: response.message, count: response.count });
        handleResetAll();
      } else {
        setImportResult({
          success: false,
          message: response?.message || "Échec de l'import des participants.",
        });
      }
    } catch (error) {
      setImportResult({
        success: false,
        message: error?.response?.data?.message || "Erreur lors de l'import des participants.",
      });
    } finally {
      setIsImporting(false);
    }
  };

  useEffect(() => {
    let updatedData = [];
    if (selectedOption === "a") {
      updatedData = tableData; // Affiche toutes les données
    } else if (selectedOption === "b") {
      updatedData = tableData.filter((item) => item.suspension_part === "W"); // Affiche les données actives
    } else if (selectedOption === "c") {
      updatedData = tableData.filter((item) => item.suspension_part === "A"); // Affiche les données suspendues
    } else if (selectedOption === "d") {
      updatedData = tableData.filter((item) => item.suspension_part === "S"); // Affiche les données agréées
    } else if (selectedOption === "e") {
      updatedData = tableData.filter((item) => item.suspension_part === "R"); // Affiche les données non agréées
    }
    setFilteredData(updatedData); // Mettre à jour les données filtrées
  }, [selectedOption, tableData]);

  useEffect(() => {
    setTotalParticipants(calculatetotalParticipants(filteredData));
    // Utiliser currentPageData si disponible (après filtrage de la table), sinon filteredData
    const dataCount = currentPageData.length > 0 ? currentPageData.length : filteredData.length;
    setRowCount(dataCount);
  }, [filteredData, currentPageData]);
  const calculatetotalParticipants = (data) => {
    setNbenfant(data.reduce((acc, val) => acc + val.nbenfant, 0));
    setNbfemme(data.reduce((acc, val) => acc + val.nbfemme, 0));
    //nbepoux: codebenef === "4"
    setNbepoux(data.reduce((acc, val) => acc + val.nbepoux, 0));
    setTotalParticipants(data.length);

    return data.length;
  };

  useEffect(() => {
    if (selectedOption === "e") {
      setShowCreateDivButton(true); // Afficher le bouton si l'option est "Non agrées"
    } else {
      setShowCreateDivButton(false); // Cacher le bouton pour les autres options
    }
  }, [selectedOption]);



  const handleDelete = (numcont) => {
    confirm({
      title: `Êtes-vous sûr de vouloir supprimer le participant N° ${numcont} ?`,
      icon: <ExclamationCircleOutlined />,
      okText: "Oui",
      cancelText: "Non",
      onOk: async () => {
        try {
          await participantsIpmService.deleteParticipantIpm(numcont);
          const updatedData = filteredData.filter(
            (item) => item.numcont !== numcont
          );
          setFilteredData(updatedData);
          Modal.success({
            title: "Suppression effectuée",
            content: `Participant N° ${numcont} supprimé avec succès.`,
          });
        } catch (error) {
          console.error("Erreur lors de la suppression du participant :", error);
          Modal.error({
            title: "Erreur",
            content: "Erreur lors de la suppression.",
          });
        }
      },
      onCancel() {
        message.info("Suppression annulée.");
      },
    });
  };

  const handleDeleteSelectedParticipants = () => {
    if (selectedParticipants.length === 0) return;

    confirm({
      title: `Êtes-vous sûr de vouloir supprimer ${selectedParticipants.length} participant(s) ?`,
      icon: <ExclamationCircleOutlined />,
      okText: "Oui",
      cancelText: "Non",
      onOk: async () => {
        try {
          const listNumcont = selectedParticipants.map((p) => p.numcont);
          const response = await participantsIpmService.deleteBulkParticipants(listNumcont);

          if (response?.success) {
            const deleted = response.deleted || [];
            const updatedData = filteredData.filter(
              (item) => !deleted.includes(item.numcont)
            );
            setFilteredData(updatedData);
            setSelectedRowKeys([]);
            setSelectedParticipants([]);

            if (response.skipped && response.skipped.length > 0) {
              Modal.warning({
                title: "Suppression partielle",
                content: response.message,
              });
            } else {
              Modal.success({
                title: "Suppression effectuée",
                content: response.message,
              });
            }
          } else {
            Modal.error({
              title: "Erreur",
              content: response?.message || "Erreur lors de la suppression des participants.",
            });
          }
        } catch (error) {
          console.error("Erreur lors de la suppression en masse des participants :", error);
          Modal.error({
            title: "Erreur",
            content: "Erreur lors de la suppression des participants.",
          });
        }
      },
      onCancel() {
        message.info("Suppression annulée.");
      },
    });
  };


  const columns = [
    {
      title: "CodeSoc",
      dataIndex: "codesoc",
      align: "center",
      key: "codesoc",
      // className: "small-cell",
      ellipsis: true,
      render: (text) => <strong>{text}</strong>,
      align: "center",
      sorter: (a, b) => {
        const valueA =
          a.codesoc !== null && a.codesoc !== undefined
            ? parseInt(a.codesoc)
            : -Infinity;
        const valueB =
          b.codesoc !== null && b.codesoc !== undefined
            ? parseInt(b.codesoc)
            : -Infinity;
        return valueA - valueB;
      },
    },
    {
      title: "Matricule",
      dataIndex: "matricule",
      key: "matricule",
      ellipsis: true,
      align: "center",
      fixed: "left",
      sorter: (a, b) => {
        const valueA =
          a.matricule !== null && a.matricule !== undefined
            ? parseInt(a.matricule)
            : -Infinity;
        const valueB =
          b.matricule !== null && b.matricule !== undefined
            ? parseInt(b.matricule)
            : -Infinity;
        return valueA - valueB;
      },
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
          <span style={{ fontWeight: 500 }}>{text}</span>
          <Tooltip title="Éditer ce participant">
            <ArrowRightOutlined
              style={{
                cursor: "pointer",
                color: "#1890ff",
                fontSize: 12,
                transition: "color 0.2s",
              }}
              onClick={() => {
                navigate(`/participants/view/${record.numcont}`);
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "#40a9ff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "#1890ff";
              }}
            />
          </Tooltip>
        </div>
      ),
    },
    {
      title: (
        <div
          style={{
            backgroundColor: "#010f1c",
            color: "white",
            paddingLeft: 5,
          }}
        >
          Prénom
        </div>
      ),

      dataIndex: "prenom",
      key: "prenom",
      //  className: "small-cell",
      ellipsis: true,
      render: (text) => (
        <div
          style={{
            whiteSpace: "nowrap",
            textAlign: "left",
            paddingRight: 5,
            paddingLeft: 5,
          }}
        >
          {text}
        </div>
      ),
      sorter: (a, b) => {
        const valueA = a.prenom || "";
        const valueB = b.prenom || "";
        return valueA.localeCompare(valueB);
      },
    },
    {
      title: "Nom",
      dataIndex: "nom",
      key: "nom",
      ellipsis: true,
      align: "left",
      sorter: (a, b) => {
        const valueA = a.nom || "";
        const valueB = b.nom || "";
        return valueA.localeCompare(valueB);
      },
      render: (text) => (
        <div
          style={{
            whiteSpace: "nowrap",
            textAlign: "left",
            paddingRight: 5,
            paddingLeft: 5,
          }}
        >
          {text || " "}
        </div>
      ),
    },
    {
      title: "Sexe",
      dataIndex: "sexe",
      key: "sexe",
      ellipsis: true,
      align: "center",
      sorter: (a, b) => (a.sexe || "").localeCompare(b.sexe || ""),
      filters: [
        { text: "Masculin", value: "MA" },
        { text: "Féminin", value: "FE" },
      ],
      filteredValue: sexeFilter.length > 0 ? sexeFilter : null,
      filterIcon: (filtered) => (
        <FilterOutlined style={{ color: filtered ? "#40a9ff" : "#ffffff" }} />
      ),
      onFilter: (value, record) => {
        const sexeValue = (record.sexe || "").trim();
        return sexeValue === value;
      },
    },

    {
      title: "D.naiss",
      dataIndex: "datenaiss",
      key: "datenaiss",
      align: "center",
      render: (text) => text ? formattedDate(text) : "",

      sorter: (a, b) => {
        const valueA = a.datenaiss ? new Date(a.datenaiss).getTime() : -Infinity;
        const valueB = b.datenaiss ? new Date(b.datenaiss).getTime() : -Infinity;
        return valueA - valueB;
      },
    },

    {
      title: "Age",
      dataIndex: "agepart",
      key: "agepart",
      align: "center",
      sorter: (a, b) => {
        const valueA = a.agepart !== null && a.agepart !== undefined ? parseInt(a.agepart) : -Infinity;
        const valueB = b.agepart !== null && b.agepart !== undefined ? parseInt(b.agepart) : -Infinity;
        return valueA - valueB;
      },
      filteredValue: (ageMin !== null || ageMax !== null) ? [`${ageMin ?? ''}-${ageMax ?? ''}`] : null,
      filterIcon: (filtered) => (
        <FilterOutlined style={{ color: filtered ? "#40a9ff" : "#ffffff" }} />
      ),
      onFilterDropdownOpenChange: (open) => {
        if (open) {
          setAgeMinDraft(ageMin);
          setAgeMaxDraft(ageMax);
        }
      },
      filterDropdown: ({ setSelectedKeys, confirm, clearFilters }) => {
        const apply = () => {
          setAgeMin(ageMinDraft);
          setAgeMax(ageMaxDraft);
          const key = (ageMinDraft !== null || ageMaxDraft !== null)
            ? [`${ageMinDraft ?? ''}-${ageMaxDraft ?? ''}`]
            : [];
          setSelectedKeys(key);
          confirm();
        };

        const reset = () => {
          setAgeMin(null);
          setAgeMax(null);
          setAgeMinDraft(null);
          setAgeMaxDraft(null);
          setSelectedKeys([]);
          clearFilters?.();
          confirm({ closeDropdown: true });
        };

        return (
          <div style={{ padding: 12, width: 220 }}>
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 12, color: "#666" }}>Âge minimum</label>
              <InputNumber
                value={ageMinDraft}
                onChange={(value) => setAgeMinDraft(value)}
                min={0}
                max={150}
                placeholder="Min"
                style={{ width: "100%", marginTop: 4 }}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: "#666" }}>Âge maximum</label>
              <InputNumber
                value={ageMaxDraft}
                onChange={(value) => setAgeMaxDraft(value)}
                min={0}
                max={150}
                placeholder="Max"
                style={{ width: "100%", marginTop: 4 }}
              />
            </div>
            <Space>
              <Button
                type="primary"
                size="small"
                onClick={apply}
                disabled={ageMinDraft === null && ageMaxDraft === null}
              >
                Appliquer
              </Button>
              <Button size="small" onClick={reset}>
                Réinitialiser
              </Button>
            </Space>
          </div>
        );
      },
      onFilter: (value, record) => {
        const age = Number(record.agepart || 0);
        if (ageMin !== null && ageMax !== null) return age >= ageMin && age <= ageMax;
        if (ageMin !== null) return age >= ageMin;
        if (ageMax !== null) return age <= ageMax;
        return true;
      },
    },
    {
      title: (
        <div
          style={{
            backgroundColor: "#010f1c",
            color: "white",
            paddingLeft: 5,
          }}
        >
          Adresse
        </div>
      ),
      dataIndex: "adresse",
      key: "adresse",
      sorter: (a, b) => {
        const valueA = a.adresse || "";
        const valueB = b.adresse || "";
        return valueA.localeCompare(valueB);
      },
      ellipsis: true,
      align: "left",
      render: (text) => (
        <div
          style={{
            whiteSpace: "nowrap",
            textAlign: "left",
            paddingRight: 5,
            paddingLeft: 5,
          }}
        >
          {text}
        </div>
      ),
    },

    {
      title: "Etat",
      dataIndex: "suspension_part",
      key: "suspension_part",
      sorter: (a, b) => a.suspension_part.localeCompare(b.suspension_part),
      ellipsis: true,
      align: "center",
      render: (text) => {
        let tagColor, tagText;

        switch (text) {
          case "A":
            tagColor = " #46de04";
            tagText = "Actif";
            break;
          case "S":
            tagColor = "#f2b305";
            tagText = "Suspendu";
            break;
          case "R":
            tagColor = "#fa0202";
            tagText = "Résilié";
            break;
          case "W":
            tagColor = "#1890ff";
            tagText = "En attente";
            break;
          default:
            tagColor = "gray";
            tagText = "Inconnu";
        }

        return <Tag color={tagColor}>{tagText}</Tag>;
      },
    },
    {
      title: "Lieu naissance",
      dataIndex: "lieunaiss",
      key: "lieunaiss",
    },
    {
      title: "Emploi",
      dataIndex: "emploi",
      key: "emploi",
      sorter: (a, b) => {
        const valueA = a.emploi || "";
        const valueB = b.emploi || "";
        return valueA.localeCompare(valueB);
      },
      ellipsis: true,
      align: "center",
    },
    {
      title: "Portable",
      dataIndex: "portable",
      key: "portable",
      sorter: (a, b) => {
        const valueA = a.portable || "";
        const valueB = b.portable || "";
        return valueA.localeCompare(valueB);
      },
      ellipsis: true,
      align: "center",
    },
    {
      title: "Nbr.Enf",
      dataIndex: "nbenfant",
      key: "nbenfant",
      sorter: (a, b) => a.nbenfant - b.nbenfant,
      /*  const valueA = a.nbenfant || "";
        const valueB = b.nbenfant || "";
        return valueA.localeCompare(valueB); */

      ellipsis: true,
      align: "center",
    },
    {
      title: "Nbr.Cj",
      dataIndex: "nbfemme",
      key: "nbfemme",
      sorter: (a, b) => a.nbfemme - b.nbfemme,
      ellipsis: true,
      align: "center",
    },
    {
      title: "Créé le",
      dataIndex: "datecreationpart",
      key: "datecreationpart",
      align: "center",
      width: 140,
      sorter: (a, b) => {
        const valueA = a.datecreationpart ? new Date(a.datecreationpart).getTime() : -Infinity;
        const valueB = b.datecreationpart ? new Date(b.datecreationpart).getTime() : -Infinity;
        return valueA - valueB;
      },
      filteredValue: (() => {
        const key = buildDateCreationFilterKey(dateCreationFilter, dateCreationRange);
        return key ? [key] : null;
      })(),
      filterIcon: (filtered) => (
        <FilterOutlined style={{ color: filtered ? "#40a9ff" : "#ffffff" }} />
      ),
      onFilterDropdownOpenChange: (open) => {
        if (open) {
          setDateCreationDraftFilter(dateCreationFilter);
          setDateCreationDraftRange([dateCreationRange?.[0] ?? null, dateCreationRange?.[1] ?? null]);
        }
      },
      filterDropdown: ({ setSelectedKeys, confirm, clearFilters }) => {
        const isRange = dateCreationDraftFilter === "range";
        const hasValidRange = Boolean(dateCreationDraftRange?.[0] && dateCreationDraftRange?.[1]);

        const apply = () => {
          setDateCreationFilter(dateCreationDraftFilter ?? null);
          setDateCreationRange([dateCreationDraftRange?.[0] ?? null, dateCreationDraftRange?.[1] ?? null]);
          const key = buildDateCreationFilterKey(dateCreationDraftFilter, dateCreationDraftRange);
          setSelectedKeys(key ? [key] : []);
          confirm();
        };

        const reset = () => {
          setDateCreationFilter(null);
          setDateCreationRange([null, null]);
          setDateCreationDraftFilter(null);
          setDateCreationDraftRange([null, null]);
          setSelectedKeys([]);
          clearFilters?.();
          confirm({ closeDropdown: true });
        };

        return (
          <div style={{ padding: 8, width: 280 }}>
            <Select
              value={dateCreationDraftFilter ?? undefined}
              onChange={(value) => {
                setDateCreationDraftFilter(value ?? null);
                if (value && value !== "range") {
                  const nextRange = getDateCreationRangeForPreset(value);
                  setDateCreationDraftRange(nextRange);
                  setSelectedKeys([buildDateCreationFilterKey(value, nextRange)]);
                } else if (!value) {
                  setDateCreationDraftRange([null, null]);
                  setSelectedKeys([]);
                } else {
                  setSelectedKeys([buildDateCreationFilterKey("range", dateCreationDraftRange) ?? "range:"]);
                }
              }}
              allowClear
              placeholder="Choisir une période"
              style={{ width: "100%" }}
              options={[
                { value: "today", label: "Aujourd'hui" },
                { value: "yesterday", label: "Hier" },
                { value: "this_week", label: "Cette semaine" },
                { value: "last_week", label: "Semaine passée" },
                { value: "this_month", label: "Ce mois-ci" },
                { value: "last_month", label: "Mois dernier" },
                { value: "this_year", label: "Cette année" },
                { value: "last_year", label: "Année passée" },
                { value: "range", label: "Date à date" },
              ]}
            />
            {isRange && (
              <RangePicker
                value={dateCreationDraftRange}
                onChange={(vals) => {
                  const next = vals ?? [null, null];
                  setDateCreationDraftRange(next);
                  setSelectedKeys([buildDateCreationFilterKey("range", next) ?? "range:"]);
                }}
                style={{ width: "100%", marginTop: 8 }}
                allowClear
                format="DD/MM/YYYY"
              />
            )}
            <Space style={{ marginTop: 8, width: "100%", justifyContent: "space-between" }}>
              <Button
                type="primary"
                size="small"
                onClick={apply}
                disabled={!dateCreationDraftFilter || (isRange && !hasValidRange)}
              >
                Appliquer
              </Button>
              <Button size="small" onClick={reset}>
                Réinitialiser
              </Button>
            </Space>
          </div>
        );
      },
      onFilter: (value, record) => {
        const recordDate = parseDateCreation(record?.datecreationpart);
        if (!recordDate) return false;
        return matchDateCreationByFilterValue(value, recordDate);
      },
      render: (text) => (
        <div style={{ whiteSpace: "nowrap", textAlign: "center" }}>
          {formattedDateTime(text)}
        </div>
      ),
    },
    {
      title: "Créé par",
      dataIndex: "created_by",
      key: "created_by",
      // className: "small-cell",
      ellipsis: true,
      align: "center",
      sorter: (a, b) => a.created_by.localeCompare(b.created_by),
    },
    {
      title: (
        <div
          style={{
            backgroundColor: " #010f1c",
            color: "white",
            padding: "1px",
          }}
        ></div>
      ),
      //  className: "small-cell",
      ellipsis: true,
      dataIndex: "actions",
      key: "actions",
      align: "center",
      fixed: "right",
      render: (text, record) => (
        <span>
          <Tooltip title="Afficher">
            {/*  le composant Link pour rediriger vers la page DetailsParticipant */}
            <Link to={`/participants/view/${record.numcont}`}>
              <EyeOutlined
                style={{ cursor: "pointer", color: "#67f02e", marginRight: 8 }}
              />
            </Link>
          </Tooltip>

          <Tooltip title="Modifier">
            <Link to={`/participants/edit/${record.numcont}`}>
              <EditOutlined
                style={{ cursor: "pointer", color: "orange", marginRight: 8 }}
              />
            </Link>
          </Tooltip>

          <Tooltip title="Supprimer">
            <DeleteFilled
              style={{ cursor: "pointer", color: "red" }}
              onClick={() => handleDelete(record.numcont)}
            />
          </Tooltip>
        </span>
      ),
    },
  ];

  const handleCodesocChange = async (value) => {

    setSelectedCodesoc(value);
    setLoading(true);

    if (value === null) {
      setFilteredData(tableData);
      setLoading(false);
      //  updateCounts(tableData);
    } else {
      try {
        const data = await participantsIpmService.getParticipantsSoc(value);
        setFilteredData(data);
        setLoading(false);
        //  updateCounts(data);
      } catch (error) {
        console.error("Erreur lors de la récupération des données :", error);
        setLoading(false);
      }
    }
  };

  // Fonction de gestion de la recherche avancée
  const handleAdvancedSearch = async (values) => {
    setSelectedCodesoc(values.codesoc);
    const [minAge, maxAge] = values.agepart
      ? values.agepart.split("-").map((agepart) => parseInt(agepart.trim(), 10))
      : [null, null];

    const payload = {
      codesoc: values.codesoc || null,
      nom: values.nom || null,
      prenom: values.prenom || null,
      sexe: values.sexe || null,
      matricule: values.matricule || null,
      age_search: values.agepart,
      codebenef: values.codebenef || null,
    };

    try {
      const data = await participantsIpmService.searchParticipantIpm(payload);
      setFilteredData(data);
      //  updateCounts(data);
      setShowAdvancedSearch(false);
    } catch (error) {
      console.error("Erreur lors de la recherche avancée :", error);
    }
  };

  const selectedSociete = codesocOptions.find(
    (option) => option.codesoc === selectedCodesoc
  )?.societe;

  // Sélection multiple : la case de l'entête sélectionne/désélectionne toutes les lignes
  // filtrées (currentPageData tient compte des filtres de colonnes du tableau), et non
  // uniquement celles de la page courante (comportement par défaut d'Ant Design).
  const allRowKeys = (currentPageData.length > 0 ? currentPageData : filteredData).map(
    (record) => record.numcont
  );

  // Écarte de la sélection toute clé qui ne correspond plus aux données filtrées
  // (recherche, filtre société, filtre avancé, etc.) : la sélection multiple ne doit
  // porter que sur les données actuellement filtrées.
  useEffect(() => {
    const validKeys = new Set(filteredData.map((record) => record.numcont));
    setSelectedRowKeys((prevKeys) => prevKeys.filter((key) => validKeys.has(key)));
    setSelectedParticipants((prevRows) => prevRows.filter((row) => validKeys.has(row.numcont)));
  }, [filteredData]);

  const handleSelectAllChange = (e) => {
    if (e.target.checked) {
      setSelectedRowKeys(allRowKeys);
      setSelectedParticipants(currentPageData.length > 0 ? currentPageData : filteredData);
    } else {
      setSelectedRowKeys([]);
      setSelectedParticipants([]);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    columnTitle: (
      <Checkbox
        checked={allRowKeys.length > 0 && selectedRowKeys.length === allRowKeys.length}
        indeterminate={selectedRowKeys.length > 0 && selectedRowKeys.length < allRowKeys.length}
        onChange={handleSelectAllChange}
      />
    ),
    onChange: (newSelectedRowKeys, selectedRows) => {
      setSelectedRowKeys(newSelectedRowKeys);
      setSelectedParticipants(selectedRows);
    },
  };

  return (
    <div>
      <Row>
        <Col xs={24} xl={24}>
          {/* <Card bordereud> */}

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={6} lg={8}>
              <label>
                <strong style={{ color: "#1890ff" }}>{selectedSociete}</strong>
                <br />
                Total Participants : <strong>{totalParticipants}</strong> - Enfants : <strong>{nbenfant}</strong> - Epouxses : <strong>{nbfemme}</strong> - Epoux : <strong>{nbepoux}</strong> <br />
              </label>
              <Row
                gutter={16}
                style={{
                  display: "flex",
                  textAlign: "start",
                  padding: "8px",
                  justifyContent: "flex-start",
                }}
              >
                <Col span={12}>
                  {/*  <Space> */}
                  <Input
                    className="search-input-highlight"
                    placeholder="Rechercher..."
                    allowClear
                    style={{ width: "100%" }}
                    onChange={(e) => handleSearch(e.target.value)}
                    onPressEnter={(e) => handleSearch(e.target.value)}
                    prefix={<SearchOutlined style={{ color: "#8c8c8c" }} />}
                    suffix={
                      <BorderOuterOutlined
                        style={{ fontSize: 16, color: "#1890ff", cursor: "pointer" }}
                        onClick={() => setShowAdvancedSearch(true)}
                      />
                    }
                  />
                  <AdvanceSearchModalPart
                    visible={showAdvancedSearch}
                    onCancel={() => setShowAdvancedSearch(false)}
                    onSearch={handleAdvancedSearch}
                    societeOptions={codesocOptions}
                    selectedCodesoc={selectedCodesoc}
                    setSelectedCodesoc={setSelectedCodesoc}
                  />
                </Col>
                <Col span={12}>
                  <Select
                    showSearch
                    className="select-colored-accent"
                    style={{
                      width: 340,
                      "--select-accent": "#52c41a",
                      "--select-accent-bg": "#f6ffed",
                      "--select-accent-shadow": "rgba(82, 196, 26, 0.25)",
                    }}
                    onChange={handleCodesocChange}
                    value={selectedCodesoc}
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    <Select.Option value="AA">
                      Toutes les Sociétes
                    </Select.Option>
                    {codesocOptions.map((item) => (
                      <Select.Option key={item.codesoc} value={item.codesoc}>
                        {`${item.codesoc} - ${item.societe}`}
                      </Select.Option>
                    ))}
                  </Select>
                  {/*   </Space> */}
                </Col>
              </Row>
            </Col>

            <Col xs={24} sm={18} lg={16} justify="end">
              <Row
                gutter={16}
                style={{
                  display: "flex",
                  textAlign: "end",
                  padding: "8px",
                  justifyContent: "flex-end",
                }}
              >
                <Col
                  span={24}
                  style={{
                    display: "flex",
                    textAlign: "end",
                    padding: "8px",
                    justifyContent: "flex-end",
                  }}
                >
                  <Space>
                    <Button
                      onClick={handleResetAll}
                      icon={<SyncOutlined />}
                      size="small"
                      // style={{ backgroundColor: "#67f02e", color: "white" }}
                      className="buttonRefresh"
                    >
                      Actualiser
                    </Button>
                    <Dropdown
                      overlay={
                        <Menu
                          style={{
                            borderRadius: 10,
                            overflow: "hidden",
                            boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                            padding: "4px 0",
                            minWidth: 200,
                          }}
                        >
                          <Menu.SubMenu
                            key="export"
                            title="Export"
                            icon={<ExportOutlined style={{ color: "#1890ff" }} />}
                          >
                            <Menu.Item key="export-excel" onClick={handleExportExcel}>
                              Excel
                            </Menu.Item>
                            <Menu.Item key="export-pdf" onClick={handleExportPDF}>
                              PDF
                            </Menu.Item>
                          </Menu.SubMenu>
                          <Menu.Item
                            key="import"
                            icon={<ImportOutlined style={{ color: "#1890ff" }} />}
                            onClick={handleOpenImportModal}
                          >
                            Importer
                          </Menu.Item>
                          <Menu.Divider />
                          <Menu.Item
                            key="delete"
                            icon={<DeleteFilled style={{ color: "#f5222d" }} />}
                            danger
                            disabled={selectedRowKeys.length === 0}
                            onClick={handleDeleteSelectedParticipants}
                          >
                            Supprimer la sélection {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ""}
                          </Menu.Item>
                        </Menu>
                      }
                      trigger={["click"]}
                      placement="bottomRight"
                    >
                      <Button
                        className="buttonRefresh"
                        size="small"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          verticalAlign: "middle",
                          gap: 6,
                          fontWeight: 500,
                        }}
                      >
                        Actions <DownOutlined />
                      </Button>
                    </Dropdown>
                  </Space>
                </Col>
              </Row>
              <Row
                gutter={16}
                style={{
                  display: "flex",
                  textAlign: "end",
                  padding: "8px",
                  justifyContent: "flex-end",
                }}
              >
                <Col
                  span={24}
                  style={{
                    display: "flex",
                    textAlign: "end",
                    padding: "8px",
                    justifyContent: "flex-end",
                  }}
                >
                  <Space>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: "1rem" }}>
                      {[
                        { value: "a", label: "Tout" },
                        { value: "b", label: "En attente" },
                        { value: "c", label: "Actif" },
                        { value: "d", label: "Suspendus" },
                        { value: "e", label: "Résilié" },
                      ].map((item) => (
                        <button
                          key={item.value}
                          onClick={() => setSelectedOption(item.value)}
                          style={{
                            padding: "6px 14px",
                            borderRadius: 20,
                            border: "none",
                            cursor: "pointer",
                            fontSize: 13,
                            fontWeight: 500,
                            transition: "all 0.3s ease",
                            background: selectedOption === item.value
                              ? "linear-gradient(135deg, #1890ff 0%, #0097e6 100%)"
                              : "#f0f0f0",
                            color: selectedOption === item.value ? "#fff" : "#595959",
                            boxShadow: selectedOption === item.value
                              ? "0 3px 10px rgba(24, 144, 255, 0.4)"
                              : "0 1px 3px rgba(0, 0, 0, 0.1)",
                          }}
                          onMouseEnter={(e) => {
                            if (selectedOption !== item.value) {
                              e.currentTarget.style.background = "#e0e0e0";
                              e.currentTarget.style.transform = "translateY(-1px)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (selectedOption !== item.value) {
                              e.currentTarget.style.background = "#f0f0f0";
                              e.currentTarget.style.transform = "translateY(0)";
                            }
                          }}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </Space>
                </Col>
              </Row>
            </Col>
          </Row>

          <Table
            loading={loading}
            scroll={{
              x: true,
            }}
            showSorterTooltip={false}
            style={{ cursor: "pointer", whiteSpace: "nowrap", width: "100%" }}
            className="custom-header1-table"
            bordered
            rowKey="numcont"
            rowSelection={rowSelection}
            columns={columns}
            dataSource={filteredData}
            pagination={{
              position: ["bottomRight"],
              pageSizeOptions: ["5", "10", "20", "50", "100"],
              showSizeChanger: true,
              pageSize: pageSize,
              onShowSizeChange: (current, size) => setPageSize(size),
              showTotal: (total, range) => (
                <div style={{ textAlign: "left", fontWeight: 500 }}>
                  Enregistrements : <strong>{total}</strong>
                </div>
              ),
            }}
            onChange={(pagination, filters, sorter, { currentDataSource }) => {
              // Stocker les données actuellement affichées après filtrage
              setCurrentPageData(currentDataSource);

              // Synchroniser le filtre sexe
              if (filters.sexe) {
                setSexeFilter(filters.sexe);
              } else {
                setSexeFilter([]);
              }
            }}
          />
        </Col>
      </Row>

      <Modal
        open={isImportModalVisible}
        onCancel={handleCloseImportModal}
        title="Importer des participants depuis un fichier Excel"
        width="90%"
        style={{ maxWidth: 800 }}
        footer={
          importResult ? (
            <Button onClick={handleCloseImportModal}>Fermer</Button>
          ) : (
            [
              <Button key="cancel" onClick={handleCloseImportModal}>
                Annuler
              </Button>,
              <Button
                key="submit"
                type="primary"
                onClick={handleConfirmImport}
                disabled={importRows.length === 0 || importErrors.length > 0}
                loading={isImporting}
              >
                {`Importer${importRows.length > 0 ? ` (${importRows.length})` : ""}`}
              </Button>,
            ]
          )
        }
      >
        {!importResult && (
          <>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Format du fichier attendu"
              description={
                <div>
                  <p style={{ marginBottom: 4 }}>
                    Fichier Excel (.xlsx) dont la première ligne contient exactement les
                    colonnes suivantes, dans cet ordre :
                  </p>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ borderCollapse: "collapse", marginTop: 4 }}>
                      <tbody>
                        <tr>
                          {IMPORT_PARTICIPANT_COLUMNS.map((col) => (
                            <td
                              key={col}
                              style={{
                                border: "1px solid #91caff",
                                padding: "4px 10px",
                                background: "#fff",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <code>{col}</code>
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p style={{ marginTop: 8, marginBottom: 0 }}>
                    Colonnes obligatoires : <code>{REQUIRED_PARTICIPANT_COLUMNS.join(", ")}</code>.
                    Les autres peuvent être laissées vides. Format de date attendu pour{" "}
                    <code>datenaiss</code>/<code>dateemb</code> : <code>YYYY-MM-DD</code> ou{" "}
                    <code>DD/MM/YYYY</code>.
                  </p>
                </div>
              }
            />

            <Button icon={<UploadOutlined />} onClick={() => importFileInputRef.current?.click()}>
              Sélectionner un fichier Excel
            </Button>
            <input
              type="file"
              ref={importFileInputRef}
              style={{ display: "none" }}
              accept=".xlsx, .xls"
              onChange={handleImportFileChange}
            />

            {importErrors.length > 0 && (
              <>
                <Alert
                  type="error"
                  showIcon
                  style={{ marginTop: 16 }}
                  message={`${importErrors.length} anomalie(s) détectée(s) — corrigez le fichier puis réimportez-le`}
                  action={
                    <Button
                      size="small"
                      icon={<ExportOutlined />}
                      onClick={handleExportImportErrors}
                    >
                      Exporter les anomalies
                    </Button>
                  }
                />
                <Table
                  style={{ marginTop: 12 }}
                  size="small"
                  scroll={{ x: "max-content" }}
                  pagination={{ pageSize: 10 }}
                  dataSource={importErrors.map((err, idx) => ({ ...err, key: idx }))}
                  columns={[
                    { title: "Ligne Excel", dataIndex: "rowNum", width: 100 },
                    { title: "Colonne", dataIndex: "field", width: 140 },
                    { title: "Anomalie", dataIndex: "message" },
                  ]}
                />
              </>
            )}

            {importRows.length > 0 && importErrors.length === 0 && (
              <Alert
                type="success"
                showIcon
                style={{ marginTop: 16 }}
                message={`${importRows.length} ligne(s) valide(s), prêtes à être importées.`}
              />
            )}
          </>
        )}

        {importResult && (
          <Alert
            type={importResult.success ? "success" : "error"}
            showIcon
            message={
              importResult.success
                ? `${importResult.count ?? importRows.length} participant(s) importé(s) avec succès`
                : "Échec de l'import"
            }
            description={importResult.message}
          />
        )}
      </Modal>
    </div>
  );
};

export default ParticipantIpm;
